const neutral = {
  950: '#0A0A0A',
  900: '#171717',
  800: '#262626',
  700: '#404040',
  600: '#525252',
  500: '#737373',
  400: '#A3A3A3',
  300: '#D4D4D4',
  200: '#E5E5E5',
  100: '#F5F5F5',
  50: '#FAFAFA',
  0: '#FFFFFF',
} as const

const gray = {
  950: '#0B0C0E',
  900: '#22242A',
  800: '#32353E',
  700: '#434856',
  600: '#595F73',
  500: '#828AA1',
  400: '#A0A7BA',
  300: '#C3C7D5',
  200: '#D4D8E2',
  100: '#E6E9EF',
  75: '#F3F4F7',
  50: '#F9FAFB',
  0: '#FCFCFD',
}

const blue = {
  950: '#172554',
  900: '#1E3A8A',
  800: '#1E40AF',
  700: '#1D4ED8',
  600: '#2563EB',
  500: '#3B82F6',
  400: '#60A5FA',
  300: '#93C5FD',
  200: '#BFDBFE',
  100: '#DBEAFE',
  50: '#EFF6FF',
  0: '#FFFFFF',
} as const

const green = {
  950: '#052E16',
  900: '#14532D',
  800: '#166534',
  700: '#15803D',
  600: '#16A34A',
  500: '#22C55E',
  400: '#4ADE80',
  300: '#86EFAC',
  200: '#BBF7D0',
  100: '#DCFCE7',
  50: '#F0FDF4',
  0: '#FFFFFF',
} as const

const lime = {
  950: '#1A2E05',
  900: '#365314',
  800: '#3F6212',
  700: '#4D7C0F',
  600: '#65A30D',
  500: '#84CC16',
  400: '#A3E635',
  300: '#BEF264',
  200: '#D9F99D',
  100: '#ECFCCB',
  50: '#F7FEE7',
  0: '#FFFFFF',
} as const

const amber = {
  950: '#451A03',
  900: '#78350F',
  800: '#92400E',
  700: '#B45309',
  600: '#D97706',
  500: '#F59E0B',
  400: '#FBBF24',
  300: '#FCD34D',
  200: '#FDE68A',
  100: '#FEF3C7',
  50: '#FFFBEB',
  0: '#FFFFFF',
} as const

const orange = {
  950: '#431407',
  900: '#7C2D12',
  800: '#9A3412',
  700: '#C2410C',
  600: '#EA580C',
  500: '#F97316',
  400: '#FB923C',
  300: '#FDBA74',
  200: '#FED7AA',
  100: '#FFEDD5',
  50: '#FFF7ED',
  0: '#FFFFFF',
} as const

const red = {
  950: '#450A0A',
  900: '#7F1D1D',
  800: '#991B1B',
  700: '#B91C1C',
  600: '#DC2626',
  500: '#EF4444',
  400: '#F87171',
  300: '#FCA5A5',
  200: '#FECACA',
  100: '#FEE2E2',
  50: '#FEF2F2',
  0: '#FFFFFF',
} as const

const magenta = {
  950: '#320422',
  900: '#540739',
  800: '#750A4F',
  700: '#970D66',
  600: '#DA1393',
  500: '#EF46B3',
  400: '#F268C1',
  300: '#F58ACF',
  200: '#F7A1D9',
  100: '#FBCDEA',
  50: '#FEEEF8',
  0: '#FFFFFF',
} as const

const violet = {
  950: '#2E1065',
  900: '#4C1D95',
  800: '#5B21B6',
  700: '#6D28D9',
  600: '#7C3AED',
  500: '#8B5CF6',
  400: '#A78BFA',
  300: '#C4B5FD',
  200: '#DDD6FE',
  100: '#EDE9FE',
  50: '#F5F3FF',
  0: '#fff',
} as const

const latte = {
  950: '#2D261E',
  900: '#4C4234',
  800: '#615545',
  700: '#776957',
  600: '#8E7E6A',
  500: '#A5927A',
  400: '#BCAC95',
  300: '#D3C7B6',
  200: '#E7E0D7',
  100: '#F5F1EC',
  50: '#FCFAF8',
  0: '#FFFFFF',
}

const tinting = {
  green: '#3E9230',
  green2: '#1E4717',
  brown: '#8B3201',
  brown2: '#3D1600',
  gray: '#A1A1A1',
  gray2: '#3C3C3C',
} as const

const tgbo = {
  bronze1: '#7F4123',
  bronze2: '#D69F69',
  silver1: '#73838C',
  silver2: '#D4DADE',
  gold1: '#DB8B09',
  gold2: '#EDDA72',
}

const black = '#000000'
const white = '#FFFFFF'
const text = '#32353E' // gray[800]
const light = '#595F7380' //'#ACAFB9' // gray[600]/50 flattend
const desc = '#32353e99' // gray[900]/60
const cyan = '#00FFFF'
const emerald = '#00FF00'
const deadred = '#FF0000'
const orangel = '#FFA500'

const alpha = {
  white75: `${white}BF`,
  white50: `${white}80`,
  white25: `${white}40`,
  white10: `${white}1A`,
  white5: `${white}0D`,
  white0: `${white}00`,
  black75: `${black}BF`,
  black50: `${black}80`,
  black30: `${black}4D`,
  black25: `${black}40`,
  black10: `${black}1A`,
  black5: `${black}0D`,
  black0: `${black}00`,
  text75: `${text}BF`,
  text50: `${text}80`,
  text25: `${text}40`,
  text10: `${text}1A`,
  text5: `${text}0D`,
  text0: `${text}00`,
  red75: `${red[600]}BF`,
  red50: `${red[600]}80`,
  red25: `${red[600]}40`,
  red10: `${red[600]}1A`,
  red5: `${red[600]}0D`,
  red0: `${red[600]}00`,
  blue75: `${blue[600]}BF`,
  blue50: `${blue[600]}80`,
  blue25: `${blue[600]}40`,
  blue10: `${blue[600]}1A`,
  blue5: `${blue[600]}0D`,
  blue0: `${blue[600]}00`,
  green75: `${green[600]}BF`,
  green50: `${green[600]}80`,
  green25: `${green[600]}40`,
  green10: `${green[600]}1A`,
  green5: `${green[600]}0D`,
  green0: `${green[600]}00`,
  orange75: `${orange[500]}BF`,
  orange50: `${orange[500]}80`,
  orange25: `${orange[500]}40`,
  orange10: `${orange[500]}1A`,
  orange5: `${orange[500]}0D`,
  orange0: `${orange[500]}00`,
  amber75: `${amber[600]}BF`,
  amber50: `${amber[600]}80`,
  amber25: `${amber[600]}40`,
  amber10: `${amber[600]}1A`,
  amber5: `${amber[600]}0D`,
  amber0: `${amber[600]}00`,
}

export const colorsDefs = {
  text,
  light,
  black,
  white,
  neutral,
  gray,
  blue,
  green,
  lime,
  amber,
  orange,
  red,
  magenta,
  violet,
  tinting,
  desc,
  tgbo,
  latte,
  cyan,
  emerald,
  deadred,
  orangel,
} as const

export const colors = {
  ...colorsDefs,
  ...alpha,
} as const
